import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUser } from '../../features/userSlice';
import Select from 'react-select';
import { saveAs } from 'file-saver';
import { Button, Form } from 'react-bootstrap';
import { FaEdit, FaSave, FaCheck, FaFileExport, FaExclamationCircle } from 'react-icons/fa';
import './Gstr2bUploadAndEdit.module.css';

const Gstr2bUploadAndEdit = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const token = user?.token;
  const emp_id = user?.emp_id;

  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [editedRow, setEditedRow] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // 'success' | 'danger' | 'warning' | 'info'
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [searchField, setSearchField] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const normalizeValue = (v) => {
    if (v === null || v === undefined) return "";
    if (v instanceof Date) return v.toISOString();
    if (Array.isArray(v)) {
      return v.join(", ");
    }
    if (typeof v === "object") {
      const flat = Object.values(v)[0];
      return flat !== undefined ? flat : "";
    }
    return v;
  };

  const normalizeRows = (data) => {
    return (data || []).map(row => {
      const clean = {};
      Object.entries(row).forEach(([k, v]) => {
        clean[k] = normalizeValue(v);
      });
      return clean;
    });
  };

  // ---- Fetch Periods ----
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await axios.post(
          '/api/gst2b/getPeriods',
          {
            entity_code: 'SE',
            emp_id,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const opts = (res.data || []).map(p => ({
          value: p.GSTR2B_PERIOD,
          label: p.GSTR2B_PERIOD,
        }));
        setPeriodOptions(opts);
      } catch (err) {
        console.error('Error fetching periods:', err);
        setMessageType('danger');
        setMessage('Failed to load periods.');
      }
    };

    if (token) {
      fetchPeriods();
    }
  }, [token, emp_id]);

  const handleFileChange = (e) => setFile(e.target.files[0]);

  // ---- Upload Handler (Corrected) ----
  const handleUpload = async () => {
    if (!file) {
      setMessageType('warning');
      setMessage('Please select a file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadLoading(true);
    setMessageType('info');
    setMessage('Uploading GSTR2B... Please wait.');

    try {
      const res = await axios.post('/api/gst2b/uploadGst2b', formData, {
        headers: { Authorization: `Bearer ${token}` },
        // Long DB insert may take time
        timeout: 10 * 60 * 1000, // 10 minutes
      });

      // Backend returns: { message, batchId, success: true }
      setMessageType('success');
      setMessage(res.data.message || 'GSTR2B uploaded successfully.');
      setRows([]);            // For upload, we don't need to show rows
      setEditedRow(null);
      setPage(1);

      // Optional: auto-select uploaded period if backend sends it
      // if (res.data.gstr2b_period) {
      //   const found = periodOptions.find(p => p.value === res.data.gstr2b_period);
      //   if (found) setSelectedPeriod(found);
      // }

    } catch (err) {
      console.error('Upload ERROR:', err);

      if (err.response) {
        // Backend responded with an error status
        const backendMsg =
          err.response.data?.error ||
          err.response.data?.message ||
          err.response.statusText ||
          'Server returned an error.';
        if (err.response.status === 409) {
          // Duplicate file
          setMessageType('warning');
          setMessage(`Duplicate file: ${backendMsg}`);
        } else {
          setMessageType('danger');
          setMessage(`Upload failed: ${backendMsg}`);
        }
      } else if (err.code === 'ECONNABORTED') {
        setMessageType('danger');
        setMessage('Upload timed out. The server took too long to respond.');
      } else {
        setMessageType('danger');
        setMessage('Network or server error during upload.');
      }
    } finally {
      setUploadLoading(false);
    }
  };

  // ---- Fetch Data for selected period ----
  const fetchData = async () => {
    if (!selectedPeriod) {
      setMessageType('warning');
      setMessage('Please select a period and then click Show Data.');
      return;
    }
    setDataLoading(true);
    setMessage('');
    try {
      const res = await axios.post(
        '/api/gst2b/getGst2bData',
        {
          entity_code: 'SE',
          period: selectedPeriod.value,
          emp_id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5 * 60 * 1000,
        }
      );
      setRows(normalizeRows(res.data || []));
      setEditedRow(null);
      setPage(1);

      if (!res.data || res.data.length === 0) {
        setMessageType('warning');
        setMessage('No records found for selected period.');
      } else {
        setMessageType('success');
        setMessage(`Loaded ${res.data.length} records for period ${selectedPeriod.value}.`);
      }
    } catch (err) {
      console.error('Fetch data ERROR:', err);
      if (err.response && err.response.status === 404) {
        const backendMsg = err.response.data?.message || 'No GST2B Data found.';
        setRows([]);
        setMessageType('warning');
        setMessage(backendMsg);
      } else {
        setMessageType('danger');
        setMessage('Fetch failed. Please try again.');
      }
    } finally {
      setDataLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditedRow({ ...row });
  };

  const handleFormChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!editedRow) {
      setMessageType('warning');
      setMessage('No edited row to save.');
      return;
    }
    try {
      await axios.patch(
        '/api/gst2b/update-rejected-records',
        { records: [editedRow] },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessageType('success');
      setMessage('Corrections saved successfully.');
      setEditedRow(null);
      fetchData();
    } catch (err) {
      console.error(err);
      setMessageType('danger');
      setMessage('Save failed.');
    }
  };

  const handleExportXLSX = async () => {
    if (!selectedPeriod) {
      setMessageType('warning');
      setMessage('Please select a period before exporting errors.');
      return;
    }
    try {
      const res = await axios.post(
        '/api/gst2b/export',
        {
          period: selectedPeriod.value,
          emp_id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 5 * 60 * 1000,
        }
      );
      saveAs(new Blob([res.data]), `gst2b_${selectedPeriod.value}_errors.xlsx`);
      setMessageType('success');
      setMessage('Error file exported successfully.');
    } catch (err) {
      console.error('Export ERROR:', err);
      setMessageType('danger');
      setMessage('Export failed.');
    }
  };

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
  };

  const filtered = (rows || []).filter(row => {
    if (searchField && filterText) {
      return row[searchField]?.toString().toLowerCase().includes(filterText);
    }
    return true;
  });

  if (sortField) {
    filtered.sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];
      if (valA === valB) return 0;
      return sortOrder === 'asc'
        ? valA > valB ? 1 : -1
        : valA < valB ? 1 : -1;
    });
  }

  const paginatedRows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(filtered.length / rowsPerPage) || 1;

  return (
    <div className="container mt-3">
      {/* Top Toolbar */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 toolbar-header">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            className="form-control form-control-sm"
          />
          <Button
            size="sm"
            variant="primary"
            onClick={handleUpload}
            disabled={uploadLoading}
          >
            {uploadLoading ? 'Uploading...' : 'Upload'}
          </Button>
          <Select
            className="w-auto"
            options={periodOptions}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            placeholder="Select Period"
          />
          <Button
            size="sm"
            variant="info"
            onClick={fetchData}
            disabled={dataLoading}
          >
            {dataLoading ? 'Loading...' : 'Show Data'}
          </Button>
        </div>
      </div>

      {/* Toolbar Second Row */}
      <div
        className="d-flex flex-nowrap align-items-center justify-content-between gap-2 mb-2"
        style={{ overflowX: 'auto' }}
      >
        {/* Left: Search and Action Buttons */}
        <div className="d-flex flex-nowrap align-items-center gap-2">
          <Form.Select
            size="sm"
            className="form-select form-select-sm w-auto"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="">Select Field</option>
            {rows[0] && Object.keys(rows[0]).map((field, idx) => (
              <option key={idx} value={field}>{field}</option>
            ))}
          </Form.Select>

          <input
            type="text"
            className="form-control form-control-sm w-auto"
            placeholder="Search"
            onChange={(e) => setFilterText(e.target.value.toLowerCase())}
          />

          <Button size="sm" variant="outline-success" className="me-1" onClick={handleSave}>
            <FaSave className="me-1" /> Save
          </Button>

          <Button size="sm" variant="outline-warning" className="me-1">
            <FaCheck className="me-1" /> Validate
          </Button>

          <Button size="sm" variant="outline-danger" className="me-1">
            <FaExclamationCircle className="me-1" /> Show Errors
          </Button>

          <Button size="sm" variant="outline-secondary" className="me-2" onClick={handleExportXLSX}>
            <FaFileExport className="me-1" /> Export Errors
          </Button>
        </div>

        {/* Right: Pagination Controls */}
        <div className="d-flex flex-nowrap align-items-center gap-2 ms-auto">
          <div className="btn-group">
            <Button size="sm" variant="outline-secondary" onClick={() => setPage(1)} disabled={page === 1}>⏮</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}>◀</Button>
            <span className="px-2 small align-self-center">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline-secondary" onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}>▶</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setPage(totalPages)} disabled={page === totalPages}>⏭</Button>
          </div>

          <label className="me-1 small mb-0">Records/page:</label>
          <select
            className="form-select form-select-sm w-auto"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Grid */}
      <div className="table-responsive">
        <table className="table table-bordered table-sm small" style={{ fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ whiteSpace: 'nowrap', width: '1%', textAlign: 'center' }}>Action</th>
              {rows[0] && Object.keys(rows[0]).map((col, idx) => (
                <th
                  key={idx}
                  onClick={() => handleSort(col)}
                  style={{
                    whiteSpace: 'nowrap',
                    width: '1%',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  {col} {sortField === col ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((row, rIdx) => (
              <tr key={row.id || row.ID || rIdx} style={{ fontSize: '12px' }}>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                  <Button size="sm" variant="outline-primary" onClick={() => handleEdit(row)} title="Edit">
                    <FaEdit />
                  </Button>
                </td>
                {Object.entries(row).map(([field, value], cIdx) => (
                  <td key={cIdx} style={{ whiteSpace: 'nowrap' }}>{value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Form */}
      {editedRow && (
        <div className="card mt-3 p-3">
          <h6>Edit Record</h6>
          <div className="row">
            {Object.entries(editedRow).map(([field, value], idx) => (
              <div className="col-md-4 mb-2" key={idx}>
                <label className="form-label small">{field}</label>
                <input
                  value={value || ''}
                  onChange={(e) => handleFormChange(field, e.target.value)}
                  className="form-control form-control-sm"
                />
              </div>
            ))}
          </div>
          <Button className="mt-2" size="sm" variant="success" onClick={handleSave}>
            <FaSave className="me-1" /> Update
          </Button>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div className={`alert alert-${messageType} mt-3 small`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default Gstr2bUploadAndEdit;
